import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import TicketCard, { cardRules } from './TicketCard'
import TicketPeek from './TicketPeek'
import { INSPECT_CLASS, LIT_CLASS, WON_CLASS, flashPlan, pickAny, pickCards } from './draw'
import { loadTick } from './sound'

// Jumps rather than travels, so a drawn or capped card is never visited. A sweep had to step over
// them, and late in a group that step reached ninety cards in one frame.
const FLICKER_MS = 45

// The stop: each flash held 1.23x longer than the last, 14 of them over ~3.3s. Built once.
const LAND_PLAN = flashPlan(FLICKER_MS, 1.23, 600)

/** Columns that fill a 16:9 projection most evenly, so no dead bands down the sides. */
const gridShape = (count) => {
  const columns = Math.max(1, Math.ceil(Math.sqrt(count * (16 / 9))))

  return { columns, rows: Math.ceil(count / columns) }
}

/**
 * Put `className` on exactly these card indices. `held` tracks who carries it, so a frame costs
 * the cards that moved rather than every card on the wall.
 */
const syncClass = (held, nodes, className, indices) => {
  const next = new Set(indices)

  for (const index of held.current) {
    if (!next.has(index)) nodes[index]?.classList.remove(className)
  }

  for (const index of next) {
    if (!held.current.has(index)) nodes[index]?.classList.add(className)
  }

  held.current = next
}

/**
 * Every ticket as a card, with the spotlights jumping between them. This is where the draw
 * happens: on 'land' the jumping slows and what it rests on goes back through `onLanded`.
 *
 * `mode`: 'sweep' flashes at one pace, 'land' stops, 'rest' holds what is lit, 'off' sleeps.
 * `live` comes from the page, so START is gated on the list the spotlight runs over.
 *
 * Lighting writes to the DOM node, not state: re-rendering hundreds of cards a frame would stutter.
 */
const TicketWall = ({
  cards,
  mode,
  slots,
  undrawn,
  live,
  headroom,
  won,
  wonTickets,
  winners,
  onStopped,
  onLanded,
}) => {
  const cardNodes = useRef([])
  // Which cards carry each class, so a frame only touches what changed.
  const lit = useRef(new Set())
  const darkened = useRef(new Set())
  // The card under the cursor and the box it occupies, or null.
  const [peek, setPeek] = useState(null)
  const playTick = useRef(null)

  // An effect, not a state initializer: StrictMode runs those twice and would leak an AudioContext.
  useEffect(() => {
    // No Web Audio (old Safari, disabled by policy): the draw runs without ticks rather than crash.
    if (!window.AudioContext) return undefined

    const context = new AudioContext()
    playTick.current = loadTick(context, '/sounds/tick.mp3')

    return () => context.close()
  }, [])

  const { columns, rows } = gridShape(cards.length)

  // Only at rest: a card pulling out from under a running spotlight reads as a glitch.
  const canInspect = mode === 'off' || mode === 'rest'
  const hovered = peek && cards[peek.ticket - 1]

  // Stable, or it would invalidate the deck memo it is a dependency of. A rebuilt node arrives
  // bare, so it takes its classes back from the refs.
  const onNode = useCallback((index, node) => {
    cardNodes.current[index] = node

    if (!node) return

    if (lit.current.has(index)) node.classList.add(LIT_CLASS)
    if (darkened.current.has(index)) node.classList.add(WON_CLASS)
  }, [])

  /**
   * Delegated, rather than a listener on each of several hundred cards. Holds only the ticket;
   * name and prize are read at render, so an award under the cursor cannot go stale.
   */
  const onPointerOver = (event) => {
    if (!canInspect) return

    const node = event.target.closest('[data-ticket]')

    if (!node) return

    const ticket = Number(node.dataset.ticket)

    if (!cards[ticket - 1]) return

    setPeek({ ticket, rect: node.getBoundingClientRect() })
  }

  // The panel is fixed to a rect measured on pointer arrival, so anything that moves the wall
  // without moving the pointer strands it beside the wrong card.
  useEffect(() => {
    if (!peek) return undefined

    const close = () => setPeek(null)

    // Capture, because the scroll happens on an ancestor rather than on window.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)

    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [peek])

  // Greys out drawn tickets, straight to the nodes so an award does not re-render the wall.
  // Card index is ticket - 1 by construction, so no search.
  useEffect(() => {
    const spent = []

    for (const ticket of won) spent.push(ticket - 1)

    syncClass(darkened, cardNodes.current, WON_CLASS, spent)
  }, [won])

  /**
   * The spotlight, and with it the draw. 'off' clears, 'rest' holds the winners lit, and
   * 'sweep'/'land' share the frame loop below, differing only in whether the interval grows.
   * 'land' ends by handing the cards under the last flash to `onLanded`.
   */
  useEffect(() => {
    const showAt = (indices) => syncClass(lit, cardNodes.current, LIT_CLASS, indices)

    if (cards.length === 0) return undefined

    if (mode === 'off') {
      showAt([])

      return undefined
    }

    if (mode === 'rest') {
      // Skipped while empty: that gap is where the lit cards are the only record of the landing.
      if (winners.length > 0) showAt(winners.map((winner) => winner.ticket - 1))

      return undefined
    }

    // A sweep travels every undrawn card; only a landing is confined to what can win.
    const isLanding = mode === 'land'

    if ((isLanding ? live : undrawn).length === 0) {
      if (isLanding) onLanded([])

      return undefined
    }

    // Null for a sweep, which holds one pace and never ends.
    const plan = isLanding ? LAND_PLAN : null
    let flash = 0
    let resting = []
    let nextAt = performance.now()
    let frame

    // Frame clock, not a timeout chain: at 45 ms a flash is under three frames, so timer jitter
    // would show as a third of its length.
    const tick = (now) => {
      if (now >= nextAt) {
        // The last flash has had its full time, so the cards under it are the winners.
        if (plan && flash >= plan.length) {
          onLanded(resting.map((index) => cards[index]))

          return
        }

        const picked = isLanding
          ? pickCards(live, cards, headroom, slots)
          : pickAny(undrawn, slots)

        // Nothing left that can be awarded.
        if (picked.length === 0) {
          onLanded([])

          return
        }

        showAt(picked)

        // A tick per flash. The last flash of a landing is the stop itself: the whole tick file
        // and the crash.
        if (plan && flash === plan.length - 1) {
          playTick.current?.(Infinity)
          onStopped()
        } else {
          playTick.current?.()
        }

        resting = picked
        nextAt = now + (plan ? plan[flash] : FLICKER_MS)
        flash++
      }

      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
  }, [mode, slots, cards, undrawn, live, headroom, winners, onStopped, onLanded])

  /**
   * Rebuilt only when what they show changes. STOP re-renders this component, and rebuilding 700
   * elements in that commit is the pause between the press and the spotlight moving.
   */
  const deck = useMemo(
    () =>
      cards.map((card, index) => (
        <TicketCard key={card.ticket} card={card} index={index} onNode={onNode} />
      )),
    // Not the won tickets: the effect above classes those directly, and listing them here rebuilt
    // every card on every award.
    [cards, onNode],
  )

  return (
    <Box
      className={canInspect ? INSPECT_CLASS : undefined}
      onMouseOver={onPointerOver}
      onMouseLeave={() => setPeek(null)}
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        width: '100%',
        // Stops forty cards blowing up to fill a projection. Only pools under ~60 reach it.
        maxWidth: columns * 152,
        maxHeight: rows * 88,
        mx: 'auto',
        display: 'grid',
        // minmax(0, 1fr), not 1fr: a plain 1fr grows to fit, so one long name overflows the wall.
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        // The cards' whole sheet, parsed once here rather than per card.
        ...cardRules(columns, rows),
      }}
    >
      {deck}
      {/* peek outlives its render, so a shrunken wall leaves it pointing past the end. */}
      {canInspect && hovered && (
        <TicketPeek
          rect={peek.rect}
          ticket={peek.ticket}
          companyName={hovered.companyName}
          prizeName={wonTickets.find((entry) => entry.ticket === peek.ticket)?.prizeName}
        />
      )}
    </Box>
  )
}

export default TicketWall
